import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Avatar,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  TextField,
  InputAdornment,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup
} from '@mui/material';
import { 
  Edit as EditIcon, 
  AddLocation as LocationIcon,
  Language as WebsiteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Event as EventIcon,
  Business as BusinessIcon,
  Groups as GroupsIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { generateTextContent, StabilityTextGenerationOptions } from '../../services/stabilityTextService';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  limit, 
  updateDoc, 
  serverTimestamp, 
  orderBy,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Firestore,
  Timestamp
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytesResumable, 
  getDownloadURL,
  UploadTaskSnapshot
} from 'firebase/storage';
import { app } from '../../firebase';

// Initialize Firestore and Storage
const db = getFirestore(app);
const storage = getStorage(app);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface Organization {
  id: string;
  name: string;
  description?: string;
  logo?: string;
  banner?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  industry?: string;
  socialMedia?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
  };
  owner?: {
    id: string;
    name: string;
    email: string;
    photoURL?: string;
  };
  stripeAccountId?: string;
  stripeCustomerId?: string;
  stripeConnected?: boolean;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

interface EventData {
  id: string;
  name?: string;
  title?: string;
  eventName?: string;
  startDate?: Timestamp;
  endDate?: Timestamp;
  description?: string;
  location?: string;
  organizationId?: string;
  orgId?: string;
  [key: string]: any;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const OrganizationProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useUser();
  
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [ownerInfo, setOwnerInfo] = useState<{id: string, name: string, email: string, photoURL?: string} | null>(null);
  
  // File upload states
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{logo: number, banner: number}>({logo: 0, banner: 0});

  // AI description enhancement states
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [descriptionPrompt, setDescriptionPrompt] = useState('');
  const [generatedDescription, setGeneratedDescription] = useState('');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionTone, setDescriptionTone] = useState<'professional' | 'friendly' | 'formal' | 'casual'>('professional');
  
  // Fetch organization data
  useEffect(() => {
    const fetchOrganization = async () => {
       if (!id) return;

       // Extract organization ID from the complex URL structure
       // This handles various URL patterns like:
       // /organizations/123
       // /organizations/123/profile
       // /organizations/123/subID/profile
       // /organizations/Log/97N7c1/7M6/E/97JC/4ttjw/mbe

       let orgId = id;

       // Check if this is a complex ID with slashes encoded in the URL
       if (orgId.includes('/')) {
          // Take just the first segment as the organization ID
          orgId = orgId.split('/')[0];
       }

       setLoading(true);
       setError(null);

       try {
          // First try to fetch with the exact ID
          let orgRef = doc(db, 'organizations', orgId);
          let orgSnapshot = await getDoc(orgRef);

          // If not found and it's a complex path, try with other IDs
          if (!orgSnapshot.exists() && window.location.pathname.includes('/organizations/')) {
             // Try with the entire URL path after /organizations/
             const pathname = window.location.pathname;
             const orgPath = '/organizations/';
             if (pathname.startsWith(orgPath) && pathname.length > orgPath.length) {
                const pathId = pathname.substring(orgPath.length).split('/')[0];

                orgRef = doc(db, 'organizations', pathId);
                orgSnapshot = await getDoc(orgRef);
             }

             // If still not found, try to list all organizations and find a match
             if (!orgSnapshot.exists()) {
                const orgsQuery = query(collection(db, 'organizations'), limit(10));
                const orgsSnapshot = await getDocs(orgsQuery);

                if (orgsSnapshot.docs.length > 0) {
                   // Use the first organization for now
                   const firstOrg = orgsSnapshot.docs[0];

                   orgRef = doc(db, 'organizations', firstOrg.id);
                   orgSnapshot = await getDoc(orgRef);
                   orgId = firstOrg.id;
                }
             }
          }

          if (orgSnapshot.exists()) {
             const orgData = orgSnapshot.data() as Omit<Organization, 'id'>;
             const organizationId = orgSnapshot.id;

             setOrganization({
                id: organizationId,
                ...orgData,
             });

             // Set logo and banner previews if they exist
             if (orgData.logo) {
                setLogoPreview(orgData.logo);
             }
             if (orgData.banner) {
                setBannerPreview(orgData.banner);
             }

             // Fetch owner information
             if (orgData.owner?.id) {
                const userRef = doc(db, 'users', orgData.owner.id);
                const userSnapshot = await getDoc(userRef);
                if (userSnapshot.exists()) {
                   const userData = userSnapshot.data();
                   setOwnerInfo({
                      id: userSnapshot.id,
                      name: userData.displayName || userData.name || 'Unknown User',
                      email: userData.email || 'No email',
                      photoURL: userData.photoURL,
                   });
                }
             }

             // Fetch organization events
             const eventsQuery = query(
                collection(db, 'events'),
                where('organizationId', '==', organizationId),
                orderBy('startDate', 'desc'),
                limit(5)
             );

             const eventsSnapshot = await getDocs(eventsQuery);
             const eventsData = eventsSnapshot.docs.map((doc: QueryDocumentSnapshot) => ({
                id: doc.id,
                ...doc.data(),
             }));

             setEvents(eventsData as EventData[]);
          } else {
             console.error(`Organization not found with any ID attempts. Last tried: ${orgId}`);
             setError('Organization not found');
             // Don't navigate away immediately to allow debugging
             // navigate('/organizations');
          }
       } catch (err) {
          console.error('Error fetching organization:', err);
          setError('Failed to load organization');
       } finally {
          setLoading(false);
       }
    };
    
    fetchOrganization();
  }, [id, navigate]);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested objects like socialMedia.facebook
      const [parent, child] = name.split('.');
      setOrganization(prev => {
        if (!prev) return prev;
        
        return {
          ...prev,
          [parent]: {
            ...((prev[parent as keyof Organization] as any) || {}),
            [child]: value
          }
        };
      });
    } else {
      setOrganization(prev => {
        if (!prev) return prev;
        
        return {
          ...prev,
          [name]: value
        };
      });
    }
  };
  
  // Handle file upload changes
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'banner') => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (type === 'logo') {
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
      } else {
        setBannerFile(file);
        setBannerPreview(URL.createObjectURL(file));
      }
    }
  };
  
  // Upload image to Firebase Storage
  const uploadImage = async (file: File, type: 'logo' | 'banner'): Promise<string> => {
    if (!id || !file) return '';
    
    const storageRef = ref(storage, `organizations/${id}/${type}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot: UploadTaskSnapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(prev => ({
            ...prev,
            [type]: progress
          }));
        },
        (error: any) => {
          console.error(`Error uploading ${type}:`, error);
          reject(error);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };
  
  // Save organization data
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization || !id) return;
    
    setSaveLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Upload files if selected
      let logoUrl = organization.logo;
      let bannerUrl = organization.banner;
      
      if (logoFile) {
        logoUrl = await uploadImage(logoFile, 'logo');
      }
      
      if (bannerFile) {
        bannerUrl = await uploadImage(bannerFile, 'banner');
      }
      
      // Update organization document
      const orgRef = doc(db, 'organizations', id);
      await updateDoc(orgRef, {
        ...organization,
        logo: logoUrl,
        banner: bannerUrl,
        updatedAt: serverTimestamp()
      });
      
      // Reset file states
      setLogoFile(null);
      setBannerFile(null);
      setUploadProgress({logo: 0, banner: 0});
      
      setSuccess('Organization profile updated successfully!');
    } catch (err) {
      console.error('Error saving organization:', err);
      setError('Failed to save organization profile');
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Connect to Stripe
  const connectToStripe = async () => {
    if (!organization || !id) return;
    
    try {
      setSaveLoading(true);
      // This would be replaced with your actual Stripe integration
      // For now, we'll just simulate a connection with a fake account ID
      
      const stripeAccountId = `acct_${Math.random().toString(36).substring(2, 10)}`;
      
      const orgRef = doc(db, 'organizations', id);
      await updateDoc(orgRef, {
        stripeAccountId,
        stripeConnected: true,
        updatedAt: serverTimestamp()
      });
      
      setOrganization(prev => {
        if (!prev) return prev;
        
        return {
          ...prev,
          stripeAccountId,
          stripeConnected: true
        };
      });
      
      setSuccess('Connected to Stripe successfully!');
    } catch (err) {
      console.error('Error connecting to Stripe:', err);
      setError('Failed to connect to Stripe');
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Disconnect from Stripe
  const disconnectFromStripe = async () => {
    if (!organization || !id) return;
    
    try {
      setSaveLoading(true);
      
      const orgRef = doc(db, 'organizations', id);
      await updateDoc(orgRef, {
        stripeConnected: false,
        updatedAt: serverTimestamp()
      });
      
      setOrganization(prev => {
        if (!prev) return prev;
        
        return {
          ...prev,
          stripeConnected: false
        };
      });
      
      setSuccess('Disconnected from Stripe successfully!');
    } catch (err) {
      console.error('Error disconnecting from Stripe:', err);
      setError('Failed to disconnect from Stripe');
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Assign organization owner
  const assignOwner = async (userId: string) => {
    if (!organization || !id) return;
    
    try {
      setSaveLoading(true);
      
      // Get user info
      const userRef = doc(db, 'users', userId);
      const userSnapshot = await getDoc(userRef);
      
      if (!userSnapshot.exists()) {
        setError('User not found');
        setSaveLoading(false);
        return;
      }
      
      const userData = userSnapshot.data();
      const ownerData = {
        id: userId,
        name: userData.displayName || userData.name || 'Unknown User',
        email: userData.email || 'No email',
        photoURL: userData.photoURL
      };
      
      // Update organization
      const orgRef = doc(db, 'organizations', id);
      await updateDoc(orgRef, {
        owner: ownerData,
        updatedAt: serverTimestamp()
      });
      
      setOrganization(prev => {
        if (!prev) return prev;
        
        return {
          ...prev,
          owner: ownerData
        };
      });
      
      setOwnerInfo(ownerData);
      setSuccess('Organization owner updated successfully!');
    } catch (err) {
      console.error('Error assigning owner:', err);
      setError('Failed to assign organization owner');
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Function to generate organization description using AI
  const generateOrganizationDescription = async () => {
    if (!organization || !descriptionPrompt) return;
    
    setIsGeneratingDescription(true);
    
    try {
      // Prepare the prompt with organization context
      const enhancedPrompt = `Based on this information about my organization, create a compelling and professional organization description:
      
Organization Name: ${organization.name}
Industry: ${organization.industry || 'Travel and Tourism'}
Key Information: ${descriptionPrompt}

The description should be well-structured, highlight our unique value proposition, and appeal to our target audience. It should be professional${descriptionTone === 'friendly' ? ' yet friendly and approachable' : descriptionTone === 'formal' ? ' and formal' : descriptionTone === 'casual' ? ' but conversational and engaging' : ' and polished'}.`;

      // Call the Stability AI text generation service
      const options: StabilityTextGenerationOptions = {
        prompt: enhancedPrompt,
        contentType: 'newsletter',
        tone: descriptionTone as 'professional' | 'friendly' | 'formal' | 'casual',
        wordCount: 150,
        organizationName: organization.name,
        maxTokens: 500
      };
      
      // Use the same text generation service as in ContentGenerator
      const content = await generateTextContent(options);
      
      setGeneratedDescription(content);
    } catch (error) {
      console.error('Error generating description:', error);
      setError('Failed to generate description. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Function to apply the generated description
  const applyGeneratedDescription = () => {
    if (!generatedDescription) return;
    
    setOrganization(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        description: generatedDescription
      };
    });
    
    setDescriptionDialogOpen(false);
    setSuccess('Description updated! Don\'t forget to save your changes.');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error && !organization) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  return (
    <>
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
            <Typography variant="h4">
              {organization?.name || 'Organization Profile'}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saveLoading}
              startIcon={saveLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              Save Changes
            </Button>
          </Box>
          
          {/* Success/Error messages */}
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="organization profile tabs">
              <Tab label="Basic Info" />
              <Tab label="Social Media" />
              <Tab label="Branding & Images" />
              <Tab label="Payment Settings" />
            </Tabs>
          </Box>
          
          <form onSubmit={handleSave}>
            {/* Basic Info Tab */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="name"
                    label="Organization Name"
                    fullWidth
                    variant="outlined"
                    value={organization?.name || ''}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    name="email"
                    label="Contact Email"
                    fullWidth
                    variant="outlined"
                    value={organization?.email || ''}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    name="phone"
                    label="Contact Phone"
                    fullWidth
                    variant="outlined"
                    value={organization?.phone || ''}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    name="website"
                    label="Website"
                    fullWidth
                    variant="outlined"
                    value={organization?.website || ''}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    name="address"
                    label="Address"
                    fullWidth
                    variant="outlined"
                    value={organization?.address || ''}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label="Description"
                    fullWidth
                    variant="outlined"
                    value={organization?.description || ''}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setDescriptionDialogOpen(true)}
                      startIcon={<AutoAwesomeIcon />}
                      size="small"
                    >
                      Enhance with AI
                    </Button>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Organization Owner
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar 
                        src={ownerInfo?.photoURL || ''}
                        sx={{ width: 56, height: 56, mr: 2 }}
                      >
                        {!ownerInfo?.photoURL && (ownerInfo?.name?.[0] || '?')}
                      </Avatar>
                      
                      <Box>
                        <Typography variant="subtitle1">
                          {ownerInfo?.name || 'No owner assigned'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {ownerInfo?.email || ''}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Owner assignment would typically involve a more complex user selection UI */}
                    {/* For now, we'll simulate with the current user */}
                    {currentUser && (
                      <Button
                        variant="outlined"
                        onClick={() => assignOwner(currentUser.uid)}
                        disabled={saveLoading}
                        startIcon={<PersonAddIcon />}
                      >
                        {organization?.owner?.id ? 'Change Owner' : 'Assign Yourself as Owner'}
                      </Button>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Social Media Tab */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="socialMedia.facebook"
                    label="Facebook URL"
                    fullWidth
                    variant="outlined"
                    value={organization?.socialMedia?.facebook || ''}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FacebookIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    name="socialMedia.twitter"
                    label="Twitter URL"
                    fullWidth
                    variant="outlined"
                    value={organization?.socialMedia?.twitter || ''}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TwitterIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    name="socialMedia.instagram"
                    label="Instagram URL"
                    fullWidth
                    variant="outlined"
                    value={organization?.socialMedia?.instagram || ''}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InstagramIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    name="socialMedia.linkedin"
                    label="LinkedIn URL"
                    fullWidth
                    variant="outlined"
                    value={organization?.socialMedia?.linkedin || ''}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkedInIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    name="socialMedia.youtube"
                    label="YouTube URL"
                    fullWidth
                    variant="outlined"
                    value={organization?.socialMedia?.youtube || ''}
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <YouTubeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Branding & Images Tab */}
            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Organization Logo
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {logoPreview && (
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <Box 
                          component="img"
                          src={logoPreview}
                          sx={{ 
                            width: 150,
                            height: 150,
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: 1
                          }}
                        />
                      </Box>
                    )}
                    
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                    >
                      {logoPreview ? 'Change Logo' : 'Upload Logo'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'logo')}
                      />
                    </Button>
                    
                    {uploadProgress.logo > 0 && uploadProgress.logo < 100 && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress.logo}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Banner Image
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {bannerPreview && (
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <Box 
                          component="img"
                          src={bannerPreview}
                          sx={{ 
                            width: '100%',
                            maxHeight: 200,
                            objectFit: 'cover',
                            border: '1px solid #ddd',
                            borderRadius: 1
                          }}
                        />
                      </Box>
                    )}
                    
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                    >
                      {bannerPreview ? 'Change Banner' : 'Upload Banner'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'banner')}
                      />
                    </Button>
                    
                    {uploadProgress.banner > 0 && uploadProgress.banner < 100 && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress.banner}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Alert severity="info">
                    Recommended logo size: 500x500 pixels (square). Recommended banner size: 1200x300 pixels (4:1 ratio).
                  </Alert>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Payment Settings Tab */}
            <TabPanel value={tabValue} index={3}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Stripe Integration
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1">
                    {organization?.stripeConnected 
                      ? 'Your organization is connected to Stripe for payment processing.' 
                      : 'Connect your organization to Stripe to accept payments for events and trips.'}
                  </Typography>
                  
                  {organization?.stripeAccountId && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Account ID: {organization.stripeAccountId}
                    </Typography>
                  )}
                </Box>
                
                {organization?.stripeConnected ? (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={disconnectFromStripe}
                    disabled={saveLoading}
                    startIcon={<LinkOffIcon />}
                  >
                    Disconnect from Stripe
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={connectToStripe}
                    disabled={saveLoading}
                    startIcon={<LinkIcon />}
                  >
                    Connect to Stripe
                  </Button>
                )}
              </Paper>
              
              <Alert severity="info">
                <AlertTitle>Payment Processing Information</AlertTitle>
                <Typography variant="body2">
                  Connecting to Stripe allows your organization to:
                  <ul>
                    <li>Accept payments for event tickets</li>
                    <li>Process trip bookings</li>
                    <li>Manage refunds and cancellations</li>
                    <li>View payment analytics and reporting</li>
                  </ul>
                </Typography>
              </Alert>
            </TabPanel>
          </form>
        </Box>
      </Container>
      
      <Dialog
        open={descriptionDialogOpen}
        onClose={() => setDescriptionDialogOpen(false)}
        maxWidth="lg"
      >
        <DialogTitle>
          Generate Organization Description with AI
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body1">
                Enter key information about your organization to help the AI generate a compelling description:
              </Typography>
              
              <TextField
                name="descriptionPrompt"
                label="Key Information"
                fullWidth
                variant="outlined"
                value={descriptionPrompt}
                onChange={(e) => setDescriptionPrompt(e.target.value)}
                multiline
                rows={4}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body1">
                Choose the tone for your organization description:
              </Typography>
              
              <ButtonGroup variant="contained" aria-label="outlined primary button group">
                <Button
                  onClick={() => setDescriptionTone('professional')}
                  color={descriptionTone === 'professional' ? 'primary' : 'inherit'}
                >
                  Professional
                </Button>
                <Button
                  onClick={() => setDescriptionTone('friendly')}
                  color={descriptionTone === 'friendly' ? 'primary' : 'inherit'}
                >
                  Friendly
                </Button>
                <Button
                  onClick={() => setDescriptionTone('formal')}
                  color={descriptionTone === 'formal' ? 'primary' : 'inherit'}
                >
                  Formal
                </Button>
                <Button
                  onClick={() => setDescriptionTone('casual')}
                  color={descriptionTone === 'casual' ? 'primary' : 'inherit'}
                >
                  Casual
                </Button>
              </ButtonGroup>
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={generateOrganizationDescription}
                disabled={isGeneratingDescription}
                startIcon={isGeneratingDescription ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              >
                Generate Description
              </Button>
            </Grid>
            
            {generatedDescription && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Generated Description
                </Typography>
                
                <Typography variant="body1">
                  {generatedDescription}
                </Typography>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={applyGeneratedDescription}
                >
                  Apply Description
                </Button>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDescriptionDialogOpen(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationProfile;